import { ChartRenderer } from './ChartRenderer';
import { FLOAT_COMPARISON_EPSILON, BEAT_MAP_KEY_PRECISION } from './constants';
import type { ParsedNote, ComboEvent, ChartData } from '~/types';

export class ComboCounter {
  private comboEvents: ComboEvent[];
  cumulativeComboMap: Map<string, number>;

  constructor(
    private sortedNotes: ParsedNote[],
    private allParsedNotesMap: Map<number, ParsedNote>,
    private bpms: ChartData['Bpms'],
    private chartOffset: number = 0
  ) {
    this.sortedNotes = sortedNotes;
    this.allParsedNotesMap = allParsedNotesMap;
    this.bpms = bpms;
    this.comboEvents = [];
    this.cumulativeComboMap = new Map<string, number>();
    this._initialize();
  }

  private _initialize(): void {
    this.comboEvents = this._generateComboEvents();
    this._populateCumulativeComboMap();
  }

  private _getNoteEffectiveEndTime(note: ParsedNote): number {
    if (note.holds.length > 0) {
      const lastHoldLink = note.holds[note.holds.length - 1];
      if (lastHoldLink.type === 'beat') {
        return lastHoldLink.beat;
      } else {
        // type === 'uid'
        const targetNote = this.allParsedNotesMap.get(lastHoldLink.uid);
        if (targetNote) {
          return targetNote.just;
        }
      }
    }
    return note.just;
  }

  private _generateComboEvents(): ComboEvent[] {
    const comboEvents: ComboEvent[] = [];
    const processedHoldChainBodyUIDs = new Set<number>();

    for (const currentStartingNote of this.sortedNotes) {
      if (processedHoldChainBodyUIDs.has(currentStartingNote.uid)) {
        continue;
      }

      comboEvents.push({ beat: currentStartingNote.just, value: 1 });

      if (currentStartingNote.flags.noteType === 'Hold') {
        const chainStartBeat = currentStartingNote.just;
        let lastNoteInChain = currentStartingNote;
        let nextNoteCandidate = currentStartingNote;

        while (nextNoteCandidate.holds.length > 0) {
          const lastLink = nextNoteCandidate.holds[nextNoteCandidate.holds.length - 1];
          if (lastLink.type === 'uid') {
            const targetNote = this.allParsedNotesMap.get(lastLink.uid);
            if (targetNote && targetNote.flags.noteType === 'Hold') {
              if (processedHoldChainBodyUIDs.has(targetNote.uid)) break;
              processedHoldChainBodyUIDs.add(targetNote.uid);
              lastNoteInChain = targetNote;
              nextNoteCandidate = targetNote;
            } else {
              break;
            }
          } else {
            break;
          }
        }

        const chainEffectiveEndBeat = this._getNoteEffectiveEndTime(lastNoteInChain);
        const holdDuration = Math.max(0, chainEffectiveEndBeat - chainStartBeat);

        const getBeatInterval = (referenceTimeSeconds: number) => {
          // Calculate the duration of 0.5 beats starting from referenceTimeSeconds
          const durationOfHalfBeat = ChartRenderer.convertBeatToChartSeconds(
            0.5,
            this.bpms,
            referenceTimeSeconds + this.chartOffset
          );
          // Ensure a minimum precision for the interval
          return Math.ceil(durationOfHalfBeat * 1000000) / 1000000;
        };

        if (holdDuration > FLOAT_COMPARISON_EPSILON) {
          const finalComboTickTimesSet = new Set<number>();

          const naturalIntervalTickTimesSet = new Set<number>();
          const allExplicitTickTimesInSegmentList: number[] = [];
          const isTimeAlignedToAnIntervalFramework = (
            timeToAlign: number,
            tolerance = FLOAT_COMPARISON_EPSILON
          ): boolean => {
            if (Math.abs(timeToAlign - chainStartBeat) <= tolerance) {
              return true; // Aligned with the start of the chain segment
            }
            // timeToAlign is already constrained by caller to be > chainStartBeat for this check's purpose

            let currentSimulatedIntervalTick = chainStartBeat;
            // Simulate interval ticks from chainStartBeat.
            // Loop while currentSimulatedIntervalTick is less than timeToAlign.
            while (currentSimulatedIntervalTick <= timeToAlign + tolerance) {
              const beatInterval = getBeatInterval(currentSimulatedIntervalTick);
              if (beatInterval <= FLOAT_COMPARISON_EPSILON / 100) {
                return false; // Interval too small, cannot reliably simulate further
              }
              currentSimulatedIntervalTick += beatInterval;
              if (Math.abs(currentSimulatedIntervalTick - timeToAlign) <= tolerance) {
                return true; // Found alignment
              }
            }
            return false; // Did not align
          };

          // A. Populate naturalIntervalTicksSet
          // (ticks `t` such that chainStartBeat < t < chainEffectiveEndBeat)
          let beatIntervalForNatural = getBeatInterval(chainStartBeat);
          if (beatIntervalForNatural > FLOAT_COMPARISON_EPSILON) {
            let currentNaturalTick = chainStartBeat + beatIntervalForNatural;
            while (currentNaturalTick < chainEffectiveEndBeat - FLOAT_COMPARISON_EPSILON) {
              naturalIntervalTickTimesSet.add(currentNaturalTick);
              beatIntervalForNatural = getBeatInterval(currentNaturalTick);
              if (beatIntervalForNatural <= FLOAT_COMPARISON_EPSILON / 100) break;
              currentNaturalTick += beatIntervalForNatural;
            }
          }

          // B. Populate allExplicitTickTimesInSegmentList
          const notesInChainSegment: ParsedNote[] = [];
          let noteTracerForPath: ParsedNote | undefined = currentStartingNote;
          const visitedUidsForPath = new Set<number>(); // Prevent loops in malformed data
          while (noteTracerForPath && !visitedUidsForPath.has(noteTracerForPath.uid)) {
            visitedUidsForPath.add(noteTracerForPath.uid);
            notesInChainSegment.push(noteTracerForPath);
            if (noteTracerForPath.uid === lastNoteInChain.uid) break;
            let foundNextInPath = false;
            if (noteTracerForPath.holds.length > 0) {
              const lastLinkInTracer = noteTracerForPath.holds[noteTracerForPath.holds.length - 1];
              if (lastLinkInTracer.type === 'uid') {
                const nextNoteCandidateInPath = this.allParsedNotesMap.get(lastLinkInTracer.uid);
                if (nextNoteCandidateInPath && nextNoteCandidateInPath.flags.noteType === 'Hold') {
                  noteTracerForPath = nextNoteCandidateInPath;
                  foundNextInPath = true;
                }
              }
            }
            if (!foundNextInPath) {
              noteTracerForPath = undefined; // End of this path segment
            }
          }

          for (const noteInSegment of notesInChainSegment) {
            for (const holdLink of noteInSegment.holds) {
              let explicitTickTimeSeconds: number | undefined;
              if (holdLink.type === 'beat') {
                explicitTickTimeSeconds = holdLink.beat;
              } else if (holdLink.type === 'uid') {
                const targetNote = this.allParsedNotesMap.get(holdLink.uid);
                if (targetNote) {
                  explicitTickTimeSeconds = targetNote.just;
                }
              }

              if (
                explicitTickTimeSeconds !== undefined &&
                explicitTickTimeSeconds > chainStartBeat + FLOAT_COMPARISON_EPSILON &&
                explicitTickTimeSeconds <= chainEffectiveEndBeat - FLOAT_COMPARISON_EPSILON
              ) {
                allExplicitTickTimesInSegmentList.push(explicitTickTimeSeconds);
              }
            }
          }

          // C. Add aligned explicit ticks to finalComboTickTimesSet
          //    and collect them for checking against natural ticks.
          const alignedExplicitTicksThisSegment = new Set<number>();
          for (const explicitTick of allExplicitTickTimesInSegmentList) {
            if (isTimeAlignedToAnIntervalFramework(explicitTick, 1.02 * FLOAT_COMPARISON_EPSILON)) {
              finalComboTickTimesSet.add(explicitTick);
              alignedExplicitTicksThisSegment.add(explicitTick);
            }
          }

          // D. Add unconsumed natural interval ticks to finalComboTickTimesSet
          //    A natural tick is unconsumed if no ALIGNED explicit tick (collected above) covers it.
          for (const naturalTick of naturalIntervalTickTimesSet) {
            let isCoveredByAlignedExplicit = false;
            for (const alignedExplicit of alignedExplicitTicksThisSegment) {
              if (Math.abs(naturalTick - alignedExplicit) <= FLOAT_COMPARISON_EPSILON) {
                isCoveredByAlignedExplicit = true;
                break;
              }
            }
            if (!isCoveredByAlignedExplicit) {
              finalComboTickTimesSet.add(naturalTick);
            }
          }

          // E. Add chainEffectiveEndBeat
          finalComboTickTimesSet.add(chainEffectiveEndBeat);

          // F. Convert collected tick times to combo events
          for (const tickTime of finalComboTickTimesSet) {
            // Ensure not to re-add the chainStartBeat, which is handled by the initial push.
            if (Math.abs(tickTime - chainStartBeat) > FLOAT_COMPARISON_EPSILON / 10) {
              comboEvents.push({ beat: tickTime, value: 1 });
            }
          }
        }
      }
    }

    comboEvents.sort((a, b) => {
      if (Math.abs(a.beat - b.beat) > FLOAT_COMPARISON_EPSILON / 10) {
        return a.beat - b.beat;
      }
      return b.value - a.value;
    });
    return comboEvents;
  }

  public static getBeatKey(beat: number): string {
    const keyMultiplier = Math.pow(10, BEAT_MAP_KEY_PRECISION);
    return String(Math.floor(beat * keyMultiplier) / keyMultiplier);
  }

  private _populateCumulativeComboMap(): void {
    this.cumulativeComboMap.clear();
    let runningComboTotal = 0;
    if (this.comboEvents.length > 0) {
      for (let i = 0; i < this.comboEvents.length; i++) {
        runningComboTotal += this.comboEvents[i].value;

        const currentEventBeatKey = ComboCounter.getBeatKey(this.comboEvents[i].beat);
        const isLastEventAtThisBeatTime =
          i === this.comboEvents.length - 1 ||
          ComboCounter.getBeatKey(this.comboEvents[i + 1].beat) !== currentEventBeatKey;

        if (isLastEventAtThisBeatTime) {
          this.cumulativeComboMap.set(currentEventBeatKey, runningComboTotal);
        }
      }
    }
  }

  public getCumulativeComboAtBeatKey(beatKey: string): number | undefined {
    return this.cumulativeComboMap.get(beatKey);
  }

  public getCumulativeComboUpToBeat(beat: number): number {
    let comboCountUpToThisBeat = 0;
    for (const event of this.comboEvents) {
      if (event.beat <= beat + FLOAT_COMPARISON_EPSILON / 10) {
        comboCountUpToThisBeat += event.value;
      } else {
        break;
      }
    }
    return comboCountUpToThisBeat;
  }
}
